import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useApplyTag, useTags } from "@/features/crm-fields/api";
import { SourceSelect } from "@/features/crm-fields/components/SourceSelect";
import { TagChip } from "@/features/crm-fields/components/TagChip";
import { useCreateCompany } from "./api";
import {
  COMPANY_STATUSES,
  type CompanySource,
  type CompanyStatus,
} from "./types";
import { DEFAULT_STAGE, STAGE_DEFINITIONS, STAGE_LABELS } from "@/lib/stages";
import {
  DEFAULT_COMPANY_LEAD_TYPE,
  LEAD_TYPES,
  LEAD_TYPE_DEFINITIONS,
  LEAD_TYPE_LABELS,
  type LeadType,
} from "@/lib/lead-type";

interface Props {
  open: boolean;
  onClose: () => void;
  /**
   * Gate the post-create navigation behind the app-wide unsaved-changes
   * guard. Defaults to a no-op that always allows navigating, so this
   * component works standalone (e.g. from CompaniesList's own "New company"
   * button, which isn't reachable from a dirty dashboard).
   */
  confirmNavigate?: () => boolean;
}

/**
 * Normalize a user-entered website into a URL with a scheme. People naturally
 * type bare domains ("acme.com"); we prepend https:// so the value is a usable
 * link. Returns null for empty input. We deliberately do NOT use an
 * <input type="url"> — native HTML5 validation rejects bare domains and
 * silently blocks form submission, which previously made the whole dialog
 * appear unresponsive.
 */
function normalizeWebsite(raw: string): string | null {
  const v = raw.trim();
  if (!v) return null;
  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(v)) return v;
  return `https://${v}`;
}

/**
 * Browser-aligned email sanity check. The form is noValidate so website
 * normalization can accept bare domains, which means the native type="email"
 * submit gate is also bypassed. Keep the previous email protection in JS
 * before persisting companies.email (plain text in the database).
 */
function isValidEmail(value: string): boolean {
  const match = value.match(
    /^([\w.!#$%&'*+/=?^`{|}~-]+)@([a-z\d](?:[a-z\d-]{0,61}[a-z\d])?(?:\.[a-z\d](?:[a-z\d-]{0,61}[a-z\d])?)+)$/i,
  );

  if (!match) return false;

  const [, localPart, domain] = match;
  const tld = domain.split(".").at(-1) ?? "";

  return (
    !localPart.startsWith(".") &&
    !localPart.endsWith(".") &&
    !localPart.includes("..") &&
    /^[a-z]{2,}$/i.test(tld)
  );
}

export function NewCompanyDialog({ open, onClose, confirmNavigate = () => true }: Props) {
  const navigate = useNavigate();
  const createCompany = useCreateCompany();
  const applyTag = useApplyTag();
  const { data: tags = [] } = useTags();

  const [name, setName] = useState("");
  const [website, setWebsite] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [industry, setIndustry] = useState("");
  const [status, setStatus] = useState<CompanyStatus>(DEFAULT_STAGE);
  const [leadType, setLeadType] = useState<LeadType>(DEFAULT_COMPANY_LEAD_TYPE);
  const [source, setSource] = useState<CompanySource>("manual");
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [tagToAdd, setTagToAdd] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);
  const [error, setError] = useState<string | null>(null);

  // Clear local error state whenever the dialog re-opens.
  useEffect(() => {
    if (!open) setError(null);
  }, [open]);

  const tagsById = useMemo(() => new Map(tags.map((t) => [t.id, t])), [tags]);
  const availableTags = useMemo(
    () => tags.filter((t) => !t.archived && !selectedTagIds.includes(t.id)),
    [tags, selectedTagIds],
  );

  function resetForm() {
    setName("");
    setWebsite("");
    setPhone("");
    setEmail("");
    setIndustry("");
    setStatus(DEFAULT_STAGE);
    setLeadType(DEFAULT_COMPANY_LEAD_TYPE);
    setSource("manual");
    setSelectedTagIds([]);
    setTagToAdd("");
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);

    // Guard re-entry immediately: the create + tag-apply phase keeps the
    // dialog open while it awaits, so a second submit must not start another
    // create. The ref closes the tiny gap before React applies the disabled
    // state to the button.
    if (isSubmittingRef.current) return;

    if (!name.trim()) {
      setError("Company name is required.");
      return;
    }

    const trimmedEmail = email.trim();
    if (trimmedEmail && !isValidEmail(trimmedEmail)) {
      setError("Enter a valid email address.");
      return;
    }

    if (!confirmNavigate()) return;

    isSubmittingRef.current = true;
    setSubmitting(true);
    try {
      const company = await createCompany.mutateAsync({
        name: name.trim(),
        website: normalizeWebsite(website),
        phone: phone.trim() || null,
        email: trimmedEmail.toLowerCase() || null,
        industry: industry.trim() || null,
        status,
        lead_type: leadType,
        source,
      });

      // Apply any tags picked at creation (e.g. "UGC"). Best-effort: a tag
      // failure shouldn't lose the just-created company.
      if (selectedTagIds.length > 0) {
        await Promise.allSettled(
          selectedTagIds.map((tag_id) =>
            applyTag.mutateAsync({
              tag_id,
              entity_type: "company",
              entity_id: company.id,
            }),
          ),
        );
      }

      onClose();
      resetForm();
      navigate(`/companies/${company.id}`);
    } catch (e) {
      const message = e instanceof Error ? e.message : "Create failed";
      setError(message);
    } finally {
      isSubmittingRef.current = false;
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent
        className="sm:max-w-lg"
        // Don't steal focus into the first field on open — on mobile that
        // pops the on-screen keyboard immediately and covers the form.
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>New company</DialogTitle>
          <DialogDescription>
            Add a company. You can refine address, contacts, and more after
            creation.
          </DialogDescription>
        </DialogHeader>

        {/* noValidate: validation is handled in JS. Native <input type="url">
            validation rejects bare-domain websites and silently blocks
            submission; email validity is restored in handleSubmit. */}
        <form className="space-y-4" onSubmit={handleSubmit} noValidate>
          <div>
            <Label htmlFor="company-name" className="mb-1.5 block">
              Company name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="company-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Acme Corp"
            />
          </div>

          <div>
            <Label htmlFor="company-website" className="mb-1.5 block">
              Website
            </Label>
            <Input
              id="company-website"
              type="text"
              inputMode="url"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              placeholder="acme.com"
              autoComplete="off"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="company-phone" className="mb-1.5 block">
                Phone
              </Label>
              <Input
                id="company-phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="(555) 123-4567"
                autoComplete="off"
              />
            </div>
            <div>
              <Label htmlFor="company-email" className="mb-1.5 block">
                Email
              </Label>
              <Input
                id="company-email"
                type="email"
                inputMode="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="hello@acme.com"
                autoComplete="off"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="company-industry" className="mb-1.5 block">
                Industry
              </Label>
              <Input
                id="company-industry"
                value={industry}
                onChange={(e) => setIndustry(e.target.value)}
                placeholder="SaaS, Healthcare…"
              />
            </div>
            <div>
              <Label className="mb-1.5 block">Stage</Label>
              <Select
                value={status}
                onValueChange={(v) => setStatus(v as CompanyStatus)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COMPANY_STATUSES.map((s) => (
                    <SelectItem key={s} value={s}>
                      <span title={STAGE_DEFINITIONS[s]}>{STAGE_LABELS[s]}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-1.5 block">Lead type</Label>
              <Select
                value={leadType}
                onValueChange={(v) => setLeadType(v as LeadType)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LEAD_TYPES.map((t) => (
                    <SelectItem key={t} value={t}>
                      <span title={LEAD_TYPE_DEFINITIONS[t]}>
                        {LEAD_TYPE_LABELS[t]}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-1.5 block">Source</Label>
              <SourceSelect value={source} onChange={setSource} />
            </div>
          </div>

          {/* Tags — categorize at creation (e.g. "UGC company" / "UGC brand
              deal"). Backed by the shared relay.tags system; manage the tag
              list under Settings → CRM fields. */}
          <div>
            <Label className="mb-1.5 block">Tags</Label>
            {selectedTagIds.length > 0 ? (
              <div className="mb-2 flex flex-wrap gap-1">
                {selectedTagIds.map((id) => {
                  const tag = tagsById.get(id);
                  if (!tag) return null;
                  return (
                    <TagChip
                      key={id}
                      label={tag.name}
                      color={tag.color}
                      onRemove={() =>
                        setSelectedTagIds((prev) => prev.filter((t) => t !== id))
                      }
                      removeLabel={`Remove ${tag.name}`}
                    />
                  );
                })}
              </div>
            ) : null}
            {availableTags.length > 0 ? (
              <Select
                value={tagToAdd}
                onValueChange={(id) => {
                  setSelectedTagIds((prev) => [...prev, id]);
                  setTagToAdd("");
                }}
              >
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="Add a tag…" />
                </SelectTrigger>
                <SelectContent>
                  {availableTags.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <p className="text-xs text-muted-foreground">
                {tags.length === 0
                  ? "No tags yet — create them under Settings → CRM fields."
                  : "All tags added."}
              </p>
            )}
          </div>

          {error && (
            <p className="text-sm text-destructive" role="alert">
              {error}
            </p>
          )}

          <DialogFooter className="gap-2">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting || !name.trim()}>
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Creating…
                </>
              ) : (
                <>
                  <Building2 className="mr-2 h-4 w-4" /> Create company
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
